// ChatbotService.js
import axios from 'axios';

/**
 * Service to interact with the Gemini API using Structured Outputs.
 */
const ChatbotService = {
  /**
  * Generates a response from Gemini based on the user prompt and current form data.
   * @param {string} prompt - The user's request to update a resume section.
   * @param {object} formData - The current state of the resume data.
  * @returns {object} - The structured response from Gemini.
   */
  generateResponse: async (input, formData) => {
  try {

      const jsonSchema = {
        type: "object",
        properties: {
          sections: {
            type: "array",
            items: {
              type: "object",
              properties: {
                name: { type: "string" },
                content: { type: "string" },
                button: {
                  type: "object",
                  properties: {
                    label: { type: "string" },
                    action: {
                      type: "string",
                      enum: ["updateSummary", "updateExperience", "updateSkills", "updateEducation", "updatePublications"]
                    }
                  },
                  required: ["label", "action"]
                }
              },
              required: ["name", "content", "button"]
            }
          },
          message: { type: "string" }
        },
        required: ["sections", "message"]
      };

      const fullPrompt = `
        Based on the following resume data and user request, provide an updated version for the requested sections or a general response.
        The response must strictly adhere to this JSON schema: ${JSON.stringify(jsonSchema)}

        Current Resume Data:
        ${Object.entries(formData).map(([key, value]) => `${key}: ${value}`).join('\n')}

        User Request: ${input}

        Remember to focus on resume-related topics and provide advice on how to improve the resume based on the user's current skills and experience. If the user's query is not related to resumes, gently guide them back to resume-related discussions.

        CRITICAL RULE: If the user asks to edit, add, rewrite, or update any section of their resume (like summary, skills, experience, education, publications), you MUST populate the 'sections' array with the new content for that section. Set 'name' to the section name (e.g. 'Summary', 'Skills', 'Experience', 'Education', 'Publications'), 'content' to the updated plain text content, and 'button' with action 'updateSummary', 'updateExperience', etc. Do not put the updated section content in the general 'message' field alone. Use the 'message' field only for your brief explanation of changes.
      `;

  const apiUrl = '/api/ai/gemini/chat';
      const requestData = {
  model: "gemini-2.5-flash-lite",
        messages: [{ role: "user", content: fullPrompt }],
        max_tokens: 1000,
        temperature: 0.7,
        response_mime_type: 'application/json',
        response_schema: jsonSchema,
      };
      const headers = {
        'Content-Type': 'application/json',
      };

      const response = await axios.post(apiUrl, requestData, { headers });
      const messageContent = response.data.choices[0].message.content.trim();
      console.log('ChatbotService - Raw response:', messageContent);

      // Add error handling for JSON parsing
      let parsedResponse;
      try {
        parsedResponse = JSON.parse(messageContent);
      } catch (parseError) {
        console.error('ChatbotService - Error parsing JSON:', parseError);
        console.log('ChatbotService - Invalid JSON content:', messageContent);
        return {
          sections: [],
          message: "I'm sorry, but I received an invalid response. Please try again.",
        };
      }

      // Add the full response content to each section and ensure correct action strings
      if (Array.isArray(parsedResponse.sections)) {
        parsedResponse.sections = parsedResponse.sections.map(section => {
          let action;
          switch ((section.name || '').toLowerCase()) {
            case 'summary': action = 'updateSummary'; break;
            case 'experience': action = 'updateExperience'; break;
            case 'skills': action = 'updateSkills'; break;
            case 'education': action = 'updateEducation'; break;
            case 'publications': action = 'updatePublications'; break;
            default: action = 'unknown';
          }
          return {
            ...section,
            fullContent: section.content,
            button: {
              ...section.button,
              action
            }
          };
        });
      } else {
        parsedResponse.sections = [];
      }

      return parsedResponse;
    } catch (error) {
  console.error("ChatbotService - Error interacting with Gemini:", error);
      return {
        sections: [],
        message: "I'm sorry, but I'm experiencing some technical difficulties. Please try again later."
      };
    }
  },
};

export default ChatbotService;
